import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';

import * as adminApi from '../../api/admin';
import type {
  MailProvider,
  MailSettings as MailSettingsType,
  MailSettingsUpdate,
} from '../../api/admin';
import { useAuth } from '../../auth/AuthContext';
import { SearchableSelect } from '../../components/SearchableSelect';
import { validateEmail } from '../../validation/profile';

/**
 * Mail settings (superadmin only).
 *
 * Backed by `public.platform_settings` (key='mail') overlaid on top
 * of the env-var defaults from `api/app/core/config.py`. Whatever the
 * superadmin saves here takes effect for the next outgoing email
 * (verification, invitation, test-send) — no container restart.
 *
 * Two transports:
 *   - SMTP    → MailDev (dev) / AWS SES (prod).
 *   - Mailgun → Mailgun's HTTP API (staging, while SES production
 *               access is pending).
 *
 * Secret handling on the wire (matches the backend's MailSettingsUpdate
 * semantics, identical for `smtp_password` and `mailgun_api_key`):
 *   - field left blank in the form         → keep stored value
 *   - field typed with a new value         → store new value
 *   - "Clear stored …" switch ON           → forget stored value,
 *                                            fall back to env default
 *
 * Secrets are never returned by the API; the form only shows a
 * "(set)" / "(none)" indicator next to the empty input.
 */

// Transport choices for the Provider dropdown (alphabetized by label).
const PROVIDER_OPTIONS: { value: MailProvider; label: string }[] = [
  { value: 'smtp', label: 'SMTP (MailDev / SES)' },
  { value: 'mailgun', label: 'Mailgun (HTTP API)' },
];

type FormState = {
  mail_provider: MailProvider;
  smtp_host: string;
  smtp_port: string; // string while editing; coerced to number on submit
  smtp_user: string;
  smtp_password: string;
  smtp_use_tls: boolean;
  email_from: string;
  clear_smtp_password: boolean;
  mailgun_api_key: string;
  mailgun_domain: string;
  mailgun_base_url: string;
  clear_mailgun_api_key: boolean;
  mailgun_webhook_signing_key: string;
  clear_mailgun_webhook_signing_key: boolean;
};

const empty: FormState = {
  mail_provider: 'smtp',
  smtp_host: '',
  smtp_port: '',
  smtp_user: '',
  smtp_password: '',
  smtp_use_tls: false,
  email_from: '',
  clear_smtp_password: false,
  mailgun_api_key: '',
  mailgun_domain: '',
  mailgun_base_url: '',
  clear_mailgun_api_key: false,
  mailgun_webhook_signing_key: '',
  clear_mailgun_webhook_signing_key: false,
};

function settingsToForm(s: MailSettingsType): FormState {
  return {
    mail_provider: s.mail_provider,
    smtp_host: s.smtp_host,
    smtp_port: String(s.smtp_port),
    smtp_user: s.smtp_user,
    smtp_password: '',
    smtp_use_tls: s.smtp_use_tls,
    email_from: s.email_from,
    clear_smtp_password: false,
    mailgun_api_key: '',
    mailgun_domain: s.mailgun_domain,
    mailgun_base_url: s.mailgun_base_url,
    clear_mailgun_api_key: false,
    mailgun_webhook_signing_key: '',
    clear_mailgun_webhook_signing_key: false,
  };
}

export function MailSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const [server, setServer] = useState<MailSettingsType | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.email) setTestTo(user.email);
    adminApi
      .getMailSettings()
      .then((s) => {
        setServer(s);
        setForm(settingsToForm(s));
      })
      .catch((err: unknown) =>
        setLoadError(
          (err as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? 'failed to load mail settings',
        ),
      );
  }, [user?.email]);

  const isMailgun = form.mail_provider === 'mailgun';

  // Validation — only the active provider's fields gate the Save button.
  const portNum = Number(form.smtp_port);
  const portError =
    form.smtp_port.trim() === '' ||
    !Number.isInteger(portNum) ||
    portNum < 1 ||
    portNum > 65535;
  const fromError =
    validateEmail(form.email_from) !== null && form.email_from !== '';
  const hostError = form.smtp_host.trim() === '';
  const mailgunDomainError = form.mailgun_domain.trim() === '';
  const providerInvalid = isMailgun
    ? mailgunDomainError
    : portError || hostError;
  const formInvalid = fromError || providerInvalid;

  const dirty =
    server === null
      ? false
      : form.mail_provider !== server.mail_provider ||
        form.email_from !== server.email_from ||
        form.smtp_host !== server.smtp_host ||
        form.smtp_port !== String(server.smtp_port) ||
        form.smtp_user !== server.smtp_user ||
        form.smtp_use_tls !== server.smtp_use_tls ||
        form.smtp_password !== '' ||
        form.clear_smtp_password ||
        form.mailgun_domain !== server.mailgun_domain ||
        form.mailgun_base_url !== server.mailgun_base_url ||
        form.mailgun_api_key !== '' ||
        form.clear_mailgun_api_key ||
        form.mailgun_webhook_signing_key !== '' ||
        form.clear_mailgun_webhook_signing_key;

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (server === null) return;
    setSaveError(null);
    setSavedAt(null);
    setSaving(true);
    try {
      const patch: MailSettingsUpdate = {};
      if (form.mail_provider !== server.mail_provider)
        patch.mail_provider = form.mail_provider;
      if (form.email_from !== server.email_from)
        patch.email_from = form.email_from;
      // SMTP fields
      if (form.smtp_host !== server.smtp_host) patch.smtp_host = form.smtp_host;
      if (String(server.smtp_port) !== form.smtp_port)
        patch.smtp_port = portNum;
      if (form.smtp_user !== server.smtp_user) patch.smtp_user = form.smtp_user;
      if (form.smtp_use_tls !== server.smtp_use_tls)
        patch.smtp_use_tls = form.smtp_use_tls;
      if (form.smtp_password !== '') patch.smtp_password = form.smtp_password;
      if (form.clear_smtp_password) patch.clear_smtp_password = true;
      // Mailgun fields
      if (form.mailgun_domain !== server.mailgun_domain)
        patch.mailgun_domain = form.mailgun_domain;
      if (form.mailgun_base_url !== server.mailgun_base_url)
        patch.mailgun_base_url = form.mailgun_base_url;
      if (form.mailgun_api_key !== '')
        patch.mailgun_api_key = form.mailgun_api_key;
      if (form.clear_mailgun_api_key) patch.clear_mailgun_api_key = true;
      if (form.mailgun_webhook_signing_key !== '')
        patch.mailgun_webhook_signing_key = form.mailgun_webhook_signing_key;
      if (form.clear_mailgun_webhook_signing_key)
        patch.clear_mailgun_webhook_signing_key = true;

      const next = await adminApi.updateMailSettings(patch);
      setServer(next);
      setForm(settingsToForm(next));
      setSavedAt(Date.now());
    } catch (err: unknown) {
      setSaveError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to save mail settings',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async (e: FormEvent) => {
    e.preventDefault();
    setSendError(null);
    setSentTo(null);
    setSending(true);
    try {
      await adminApi.sendTestMail(testTo.trim());
      setSentTo(testTo.trim());
    } catch (err: unknown) {
      setSendError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to send test email',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 760 }}>
      {!embedded && <Typography variant="h4">Mail settings</Typography>}

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Transport
          </Typography>

          {loadError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {loadError}
            </Alert>
          )}
          {!server && !loadError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {server && (
            <Box component="form" onSubmit={handleSave} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <SearchableSelect<MailProvider>
                    id="mail-provider"
                    label="Provider"
                    size="medium"
                    fullWidth
                    value={form.mail_provider}
                    onChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        mail_provider: v,
                      }))
                    }
                    options={PROVIDER_OPTIONS}
                    helperText={
                      isMailgun
                        ? 'Mailgun HTTP API.'
                        : 'SMTP — MailDev (dev) / AWS SES (prod).'
                    }
                    disabled={saving}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="From address"
                    fullWidth
                    type="email"
                    value={form.email_from}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email_from: e.target.value }))
                    }
                    error={fromError}
                    helperText={
                      fromError
                        ? 'Invalid email address.'
                        : 'From: header on every outgoing message.'
                    }
                    disabled={saving}
                  />
                </Grid>

                {!isMailgun && (
                  <>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <TextField
                        label="SMTP host"
                        fullWidth
                        required
                        value={form.smtp_host}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, smtp_host: e.target.value }))
                        }
                        error={hostError}
                        helperText={
                          hostError
                            ? 'Host is required.'
                            : 'e.g. maildev (dev) · email-smtp.us-east-1.amazonaws.com (SES)'
                        }
                        disabled={saving}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        label="Port"
                        fullWidth
                        required
                        type="number"
                        inputProps={{ min: 1, max: 65535 }}
                        value={form.smtp_port}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, smtp_port: e.target.value }))
                        }
                        error={portError}
                        helperText={
                          portError
                            ? '1–65535'
                            : 'maildev: 1025 · SES STARTTLS: 587'
                        }
                        disabled={saving}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="SMTP username"
                        fullWidth
                        value={form.smtp_user}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, smtp_user: e.target.value }))
                        }
                        helperText="Leave blank for maildev (no auth)."
                        disabled={saving}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="SMTP password"
                        fullWidth
                        type="password"
                        value={form.smtp_password}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            smtp_password: e.target.value,
                          }))
                        }
                        placeholder={
                          server.has_smtp_password
                            ? '(set — leave blank to keep)'
                            : '(none)'
                        }
                        helperText={
                          form.clear_smtp_password
                            ? 'Password will be cleared on save.'
                            : 'Leave blank to keep the existing value.'
                        }
                        disabled={saving || form.clear_smtp_password}
                      />
                      {server.has_smtp_password && (
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Switch
                              size="small"
                              checked={form.clear_smtp_password}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  clear_smtp_password: e.target.checked,
                                  smtp_password: e.target.checked
                                    ? ''
                                    : f.smtp_password,
                                }))
                              }
                              disabled={saving}
                            />
                          }
                          label={
                            <Typography variant="caption">
                              Clear stored password
                            </Typography>
                          }
                        />
                      )}
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.smtp_use_tls}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                smtp_use_tls: e.target.checked,
                              }))
                            }
                            disabled={saving}
                          />
                        }
                        label="Use STARTTLS"
                      />
                    </Grid>
                  </>
                )}

                {isMailgun && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Mailgun domain"
                        fullWidth
                        required
                        value={form.mailgun_domain}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            mailgun_domain: e.target.value,
                          }))
                        }
                        error={mailgunDomainError}
                        helperText={
                          mailgunDomainError
                            ? 'Sending domain is required.'
                            : 'e.g. sandboxXXXX.mailgun.org or a verified domain'
                        }
                        disabled={saving}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Mailgun base URL"
                        fullWidth
                        value={form.mailgun_base_url}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            mailgun_base_url: e.target.value,
                          }))
                        }
                        helperText="US: https://api.mailgun.net/v3 · EU: https://api.eu.mailgun.net/v3"
                        disabled={saving}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Mailgun API key"
                        fullWidth
                        type="password"
                        value={form.mailgun_api_key}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            mailgun_api_key: e.target.value,
                          }))
                        }
                        placeholder={
                          server.has_mailgun_api_key
                            ? '(set — leave blank to keep)'
                            : '(none)'
                        }
                        helperText={
                          form.clear_mailgun_api_key
                            ? 'API key will be cleared on save.'
                            : 'Leave blank to keep the existing value.'
                        }
                        disabled={saving || form.clear_mailgun_api_key}
                      />
                      {server.has_mailgun_api_key && (
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Switch
                              size="small"
                              checked={form.clear_mailgun_api_key}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  clear_mailgun_api_key: e.target.checked,
                                  mailgun_api_key: e.target.checked
                                    ? ''
                                    : f.mailgun_api_key,
                                }))
                              }
                              disabled={saving}
                            />
                          }
                          label={
                            <Typography variant="caption">
                              Clear stored API key
                            </Typography>
                          }
                        />
                      )}
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Mailgun webhook signing key"
                        fullWidth
                        type="password"
                        value={form.mailgun_webhook_signing_key}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            mailgun_webhook_signing_key: e.target.value,
                          }))
                        }
                        placeholder={
                          server.has_mailgun_webhook_signing_key
                            ? '(set — leave blank to keep)'
                            : '(none)'
                        }
                        helperText={
                          form.clear_mailgun_webhook_signing_key
                            ? 'Signing key will be cleared on save.'
                            : 'Mailgun → Settings → Webhooks → HTTP webhook signing key. Required for the delivered / bounced counts on the platform-admin email card — without it every inbound event is rejected.'
                        }
                        disabled={
                          saving || form.clear_mailgun_webhook_signing_key
                        }
                      />
                      {server.has_mailgun_webhook_signing_key && (
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Switch
                              size="small"
                              checked={form.clear_mailgun_webhook_signing_key}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  clear_mailgun_webhook_signing_key:
                                    e.target.checked,
                                  mailgun_webhook_signing_key: e.target.checked
                                    ? ''
                                    : f.mailgun_webhook_signing_key,
                                }))
                              }
                              disabled={saving}
                            />
                          }
                          label={
                            <Typography variant="caption">
                              Clear stored signing key
                            </Typography>
                          }
                        />
                      )}
                    </Grid>
                  </>
                )}
              </Grid>

              {saveError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {saveError}
                </Alert>
              )}
              {savedAt !== null && !saveError && !dirty && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Saved. New settings take effect immediately.
                </Alert>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={!dirty || saving || formInvalid}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setForm(settingsToForm(server))}
                  disabled={!dirty || saving}
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Values are stored in <code>public.platform_settings</code>{' '}
            and override the container's env-var defaults. To go back
            to env-only behaviour, clear the secret and reset every
            field to match the env (or delete the row directly in
            Postgres).
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Send a test email
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 2 }}
          >
            Uses the currently saved settings (not your unsaved edits).
            Save first if you want to test new values.
          </Typography>

          <Box component="form" onSubmit={handleTestSend}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Recipient"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                error={testTo.trim() !== '' && validateEmail(testTo) !== null}
                helperText={
                  testTo.trim() !== '' && validateEmail(testTo)
                    ? validateEmail(testTo)!
                    : ' '
                }
                disabled={sending}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  sending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                disabled={
                  sending ||
                  testTo.trim() === '' ||
                  validateEmail(testTo) !== null
                }
              >
                Send
              </Button>
            </Stack>
          </Box>

          {sentTo && !sendError && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Test email sent to <strong>{sentTo}</strong>. In dev,
              check the maildev UI at{' '}
              <code>http://localhost:1080</code>.
            </Alert>
          )}
          {sendError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {sendError}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
